import { readEntitiesByIDs, watchEntitiesByIDs, readAllEntities, watchAllEntities } from '../server/entity-reads.js';
import { A } from '@adornis/base/env-info.js';
import { logger } from '@adornis/base/logging.js';
import { Int, ID } from '@adornis/baseql/baseqlTypes.js';
import { registerMutation, registerQuery, registerSubscription } from '@adornis/baseql/metadata/register.js';
import { scopedCollection } from '../server/scoped-collection.js';
import { context } from '@adornis/baseql/server/context.js';
import { getContextFromRequest } from '@adornis/baseql/server/getContext.js';
import { runInContext } from '@adornis/baseql/server/server.js';
import { watchEntireCollection } from '@adornis/baseql/server/watchQuery.js';
import { type BaseQLSelectionSet, type NarrowedEntity, selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { runWithConcurrency } from '@adornis/bh-util/concurrency.js';
import { checkAuthorizedMiddleware } from '@adornis/bh-util/middlewares/checkAuthorizedMiddleware.js';
import {
  getCurrentUserServerOrFail,
  loadRawEntityDataByIDOrFail,
  saveEntityData,
  type ServerCachedUser,
} from '@adornis/bh-util/util.js';
import type { AdornisFile } from '@adornis/file-utils/db/files.js';
import { AdornisTemplater } from '@adornis/templater/server/index.js';
import { Language } from '@adornis/translation-core/languages.js';
import { format } from 'date-fns';
import { de, enUS } from 'date-fns/locale';
import { Router } from 'express';
import { switchMap, map, of } from 'rxjs';
import { Appointment } from '../entities/appointment.js';
import { Certificate, CertificateVariable } from '../entities/certificate.js';
import { type Einzelberatung, EinzelberatungSlot } from '../entities/einzelberatung.js';
import { Institution } from '../entities/institution.js';
import { MailTemplateType } from '../entities/mail-templates.js';
import { Participation } from '../entities/Participation.js';
import { buildifyHtmlToReadableText, CertificateAttachedProduct, Product } from '../entities/products.js';
import { TextMultipleLangItem } from '../entities/TextMultipleLangItem.js';
import { User } from '../entities/user.js';
import { Workshop } from '../entities/workshop.js';
import { ParticipationState } from '../enums/enums.js';
import { Permission } from '../enums/roles.js';
import { NoParticipantsAvailableError } from '../errors/NoParticipantsAvailableError.js';
import { notifyNuzterTeilnahme } from '../notification/nutzer-teilnahme.js';
import { mongo } from '../server/mongo.js';
import { isDefined, toBerlinDate } from '../util.js';
import { getMailTemplateForInstitution } from './mail-templates.js';
import { getProductByID } from './product.js';
import { getWorkshopByID } from './workshop.js';
import { getUserByID } from './users.js';

const subscribeCertificatesByInstitution = registerSubscription({
  operationName: 'subscribeCertificatesByInstitution',
  params: [{ name: 'institutionID', type: () => String }],
  type: () => [Certificate],
  middlewares: [
    checkAuthorizedMiddleware(({ user, params }) =>
      user.hasPermission(Permission.VIEW_ADMIN_INSTITUTION_WORKSHOPS, params.institutionID),
    ),
  ],
  resolve: (institutionID: string) => {
    return (gqlFields: BaseQLSelectionSet<Certificate>) => {
      return watchEntireCollection(Certificate._collectionName).pipe(
        switchMap(async () => {
          const collection = await scopedCollection<Certificate>(Certificate._class);
          const result = await collection.find<Certificate>({ institutionID }).toArray();
          return result;
        }),
      );
    };
  },
});

export const sendCertificateToParticipant = registerMutation({
  operationName: 'sendCertificateToParticipant',
  params: [{ name: 'participationID', type: () => String }],
  type: () => Int,
  resolve: (participationID: string) => {
    return async () => {
      const participation = await loadRawEntityDataByIDOrFail(Participation, participationID, {
        _id: 1,
        _class: 1,
        productID: 1,
        userID: 1,
        gotCertificate: 1,
      });

      const product = await getProductByID(participation.productID)({
        _id: 1,
        institutionID: 1,
        attachedCertificate: { exportDate: 1, certificateID: 1 },
        slots: selectionSet(() => EinzelberatungSlot, 2),
      });
      if (!product) throw new Error('Cannot find product for ID ' + participation.productID);
      if (!context.serverContext) {
        const currentUser = await getCurrentUserServerOrFail();
        if (!currentUser.hasPermission(Permission.MANAGE_PARTICIPATION, product.institutionID))
          throw new Error('not authorized');
      }
      if (!product.attachedCertificate?.certificateID) throw new Error('cert_gen_failed_missing_cert');

      // check if sending mails is enabled
      const mailTemplate = await getMailTemplateForInstitution(
        product.institutionID,
        MailTemplateType.NUTZER_TEILNAHME,
      )({ _id: 1, whatsappNotification: 1, emailNotification: 1 });

      if (!mailTemplate.whatsappNotification && !mailTemplate.emailNotification) {
        throw new Error('cert_mails_not_enabled');
      }

      await notifyNuzterTeilnahme(participationID, true);
      participation.gotCertificate = new Date();
      await saveEntityData(participation);

      return 1;
    };
  },
});

export const sendCertificateToProductParticipantsWhichHaveParticipated = registerMutation({
  operationName: 'sendCertificateToProductParticipantsWhichHaveParticipated',
  type: () => Int,
  params: [{ name: 'productID', type: () => String }],
  resolve: (productID: string) => {
    return async () => {
      const product = await getProductByID(productID)({
        _id: 1,
        name: 1,
        institutionID: 1,
        appointments: selectionSet(() => Appointment, 2),
        capacity: 1,
        attachedCertificate: { exportDate: 1, certificateID: 1 },
      });
      if (!product) throw new Error('Cannot find product for ID ' + productID);
      if (!context.serverContext) {
        const currentUser = await getCurrentUserServerOrFail();
        if (!currentUser.hasPermission(Permission.MANAGE_PARTICIPATION, product.institutionID))
          throw new Error('not authorized');
      }

      const allParticipations = await mongo()
        .collection<Participation>(Participation._collectionName)
        .find({ productID, state: ParticipationState.participated })
        .toArray();
      if (!allParticipations.length) throw new NoParticipantsAvailableError();

      // All participations share the same productID/institution, so the shared `bhBaseQlContext.colorDefinitions`
      // write from `notifyNuzterTeilnahme` is safe under concurrency.
      const CERTIFICATE_SEND_CONCURRENCY = 10;
      const sendResults = await runWithConcurrency(allParticipations, CERTIFICATE_SEND_CONCURRENCY, participation =>
        sendCertificateToParticipant(participation._id)(),
      );

      const participationWithErrors: string[] = [];
      for (let i = 0; i < sendResults.length; i++) {
        const result = sendResults[i]!;
        if (!result.ok) {
          logger.error(result.error);
          participationWithErrors.push(allParticipations[i]!._id);
        }
      }

      if (!product.attachedCertificate) {
        throw new Error('Cannot send certificate without attached certificate');
      }

      product.attachedCertificate.exportDate = new Date();
      await product.save();

      if (participationWithErrors.length) {
        throw new Error('Could not send mail to ' + participationWithErrors.length + ' participants');
      }

      return 1;
    };
  },
});

export async function renderCertificateTemplate(
  certificateTemplateFile: NarrowedEntity<AdornisFile, { _id: 1; meta: { extension: 1 } }>,
  product: NarrowedEntity<
    Product,
    {
      _id: 1;
      name: 1;
      attachedCertificate: { descriptionsHTML: { langKey: 1; text: 1 } };
      translatedDescriptionHTML: { langKey: 1; text: 1 };
      appointnents: { name: 1; start: 1; end: 1; locatioin: 1 };
    }
  >,
  user: NarrowedEntity<User, { _id: 1; firstName: 1; title: 1; lastName: 1; email: 1; dateOfBirth: 1 }>,
) {
  const variables = fillVariablesForCertificateTemplate(product, user);
  const templater = AdornisTemplater.fromFile(certificateTemplateFile.getFullPath());
  templater.render(variables);

  const pdfBuffer = await templater.toPdfBuffer();
  const { protectPdfBuffer } = await import('../server/pdf-protection.js');
  return protectPdfBuffer(pdfBuffer);
}

function fillVariablesForCertificateTemplate(
  product: NarrowedEntity<
    Product,
    {
      name: 1;
      attachedCertificate: { descriptionsHTML: { langKey: 1; text: 1 } };
      translatedDescriptionHTML: { langKey: 1; text: 1 };
      appointnents: { start: 1; end: 1; location: 1; name: 1 };
    }
  >,
  user: NarrowedEntity<User, { title: 1; firstName: 1; lastName: 1; email: 1; dateOfBirth: 1 }>,
) {
  // Extract appointment dates for new variables
  const startDate =
    product instanceof Workshop && product.appointments?.length > 0
      ? product.appointments
          .map(ap => ap.start)
          .filter(isDefined)
          .sort((a, b) => a.getTime() - b.getTime())
          .at(0)
        ? format(
            toBerlinDate(
              product.appointments
                .map(ap => ap.start)
                .filter(isDefined)
                .sort((a, b) => a.getTime() - b.getTime())
                .at(0)!,
            ),
            'dd.MM.yyyy',
          )
        : undefined
      : (product as Einzelberatung).slotConfig?.start
        ? format(toBerlinDate((product as Einzelberatung).slotConfig!.start), 'dd.MM.yyyy')
        : undefined;

  const endDate =
    product instanceof Workshop && product.appointments?.length > 0
      ? product.appointments
          .map(ap => ap.end)
          .filter(isDefined)
          .sort((a, b) => b.getTime() - a.getTime())
          .at(0)
        ? format(
            toBerlinDate(
              product.appointments
                .map(ap => ap.end)
                .filter(isDefined)
                .sort((a, b) => b.getTime() - a.getTime())
                .at(0)!,
            ),
            'dd.MM.yyyy',
          )
        : undefined
      : (product as Einzelberatung).slotConfig?.end
        ? format(toBerlinDate((product as Einzelberatung).slotConfig!.end), 'dd.MM.yyyy')
        : undefined;

  const zeitraum = (() => {
    if (!startDate || !endDate) return '';
    if (!startDate && endDate) return endDate;
    if (startDate && !endDate) return startDate;
    if (startDate === endDate) return startDate;
    return `${startDate} - ${endDate}`;
  })();

  const termineDatum =
    product instanceof Workshop
      ? Array.from(
          new Set(
            (product.appointments ?? [])
              .map(ap => ap.start)
              .filter(isDefined)
              .sort((a, b) => a.getTime() - b.getTime())
              .map(start => format(toBerlinDate(start), 'dd.MM.yyyy')),
          ),
        ).join(' & ')
      : (product as Einzelberatung).slotConfig?.start
        ? format(toBerlinDate((product as Einzelberatung).slotConfig!.start), 'dd.MM.yyyy')
        : '';

  function termineDatumUhrzeit(locale: string) {
    return product instanceof Workshop
      ? (() => {
          const weekdays = product.appointments
            .map(ap =>
              ap.start ? format(toBerlinDate(ap.start), 'EEE', { locale: locale === 'de' ? de : enUS }) : null,
            )
            .filter(isDefined);
          const dates = product.appointments
            .map(ap => (ap.start ? format(toBerlinDate(ap.start), 'dd.MM.yyyy') : null))
            .filter(isDefined);
          const times = product.appointments
            .map(
              ap =>
                (ap.start ? format(toBerlinDate(ap.start), 'HH:mm') : '') +
                ' - ' +
                (locale === 'de'
                  ? ap.end
                    ? format(toBerlinDate(ap.end), 'HH:mm') + ' Uhr'
                    : ''
                  : ap.end
                    ? format(toBerlinDate(ap.end), 'HH:mm a')
                    : ''),
            )
            .filter(isDefined);

          const dayString = weekdays.every(wd => wd === weekdays[0]) ? weekdays[0] : weekdays.join('/');
          const datesString = dates.every(d => d === dates[0]) ? dates[0] : dates.join(' & ');
          const timesString = times.every(t => t === times[0]) ? times[0] : times.join('/');
          return `${dayString}, ${datesString} (${timesString})`;
        })()
      : (product as Einzelberatung).slotConfig?.start
        ? format(toBerlinDate((product as Einzelberatung).slotConfig!.start), 'dd.MM.yyyy')
        : '';
  }

  return {
    [CertificateVariable.User_Vorname]: user.firstName,
    [CertificateVariable.User_Nachname]: user.lastName,
    [CertificateVariable.Produkt_Titel]: product.name,
    [CertificateVariable.User_Geburtstag]: user.dateOfBirth
      ? format(toBerlinDate(user.dateOfBirth), 'dd.MM.yyyy')
      : undefined,
    [CertificateVariable.Produkt_Beschreibung_DE]: buildifyHtmlToReadableText(
      // @ts-expect-error actually I have no idea why that is not working
      TextMultipleLangItem.findByLanguage(product.translatedDescriptionHTML ?? [], Language.GERMAN)?.text ?? '',
    ),
    [CertificateVariable.Produkt_Beschreibung_EN]: buildifyHtmlToReadableText(
      // @ts-expect-error actually I have no idea why that is not working
      TextMultipleLangItem.findByLanguage(product.translatedDescriptionHTML ?? [], Language.ENGLISH)?.text ?? '',
    ),
    [CertificateVariable.Zertifikat_Beschreibung_DE]: buildifyHtmlToReadableText(
      // @ts-expect-error actually I have no idea why that is not working
      TextMultipleLangItem.findByLanguage(product.attachedCertificate?.descriptionsHTML ?? [], Language.GERMAN)?.text ??
        '/',
    ),
    [CertificateVariable.Zertifikat_Beschreibung_EN]: buildifyHtmlToReadableText(
      // @ts-expect-error actually I have no idea why that is not working
      TextMultipleLangItem.findByLanguage(product.attachedCertificate?.descriptionsHTML ?? [], Language.ENGLISH)
        ?.text ?? '/',
    ),
    [CertificateVariable.Erstellungs_Datum]: format(toBerlinDate(new Date()), 'dd.MM.yyyy'), //product.getLastExportDate,
    [CertificateVariable.Startdatum]: startDate ?? '',
    [CertificateVariable.Enddatum]: endDate ?? '',
    [CertificateVariable.Zeitraum]: zeitraum,
    [CertificateVariable.Termine_Datum_Uhrzeit]: termineDatumUhrzeit(Language.GERMAN) ?? '',
    [CertificateVariable.Termine_Datum]: termineDatum,
    [CertificateVariable.Termine_Datum_Uhrzeit_EN]: termineDatumUhrzeit(Language.ENGLISH) ?? '',
  };
}

export const getFileIDByCertificateID = registerQuery({
  operationName: 'getFileIDByCertificateID',
  type: () => String,
  params: [{ name: 'certificateID', type: () => String }],
  resolve: (certificateID: string) => {
    return async () => {
      const collection = mongo().collection<Certificate>(Certificate._collectionName);
      const result = await collection.raw.findOne<Certificate>({ _id: certificateID });
      if (result && !context.serverContext) {
        const currentUser = await getCurrentUserServerOrFail();
        if (!currentUser.hasPermission(Permission.VIEW_ADMIN_INSTITUTION_WORKSHOPS, result.institutionID))
          throw new Error('not authorized');
      }
      return result?.fileID;
    };
  },
});

export function getCertificatePreviewUrl(certificateID: string) {
  return A.absoluteUrl(`/api/certificate-preview/${certificateID}`);
}

export function getCertificateDownloadUrl(userID: string, workshopID: string) {
  return A.absoluteUrl(`/api/certificate/${userID}/${workshopID}`);
}

export function certificatePreviewRouter() {
  const router = Router();

  async function renderCertificate(
    workshop: NarrowedEntity<
      Product,
      {
        _id: 1;
        name: 1;
        attachedCertificate: {
          descriptionsHTML: { langKey: 1; text: 1 };
          certificate: { _id: 1; file: { _id: 1; meta: { extension: 1 } } };
        };
        translatedDescriptionHTML: { langKey: 1; text: 1 };
        appointnents: { name: 1; start: 1; end: 1; location: 1 };
      }
    >,
    user: NarrowedEntity<User, { _id: 1; email: 1; title: 1; firstName: 1; lastName: 1; dateOfBirth: 1 }>,
  ) {
    const certificateTemplate = workshop.attachedCertificate?.certificate;
    if (!certificateTemplate) throw new Error('Cannot generate certificate for workshop without linked certificate');
    if (!certificateTemplate?.file) throw new Error(`Cetrificate ${certificateTemplate._id}`);
    const fileBuffer = await renderCertificateTemplate(certificateTemplate.file, workshop, user);
    return fileBuffer;
  }

  router.use('/api/certificate-preview', async (req, res, next) => {
    const certificateID = req.path.split('/')[1];
    if (!certificateID) {
      logger.error('No certificateID given for certificate preview');
      return res.sendStatus(422);
    }

    const requestContext = await getContextFromRequest(req);
    if (!requestContext?.userID) return res.sendStatus(401);
    const mayPreview = await runInContext(requestContext.sessionID, async () => {
      const currentUser = await getCurrentUserServerOrFail();
      return currentUser.isAdmin;
    });
    if (!mayPreview) return res.sendStatus(403);

    const certificate = await getCertificateByID(certificateID)({
      _id: 1,
      fileID: 1,
      file: { _id: 1, meta: { extension: 1 } },
    });
    if (!certificate) throw new Error('No certificate found for ID ' + certificateID);

    const musterWorkshop = new Workshop({
      name: 'Muster-Workshop',
      description:
        'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Donec posuere pulvinar urna, eu tincidunt ipsum. Maecenas a finibus elit. Nullam tincidunt pharetra ornare. Donec sit amet lorem pellentesque, imperdiet purus ut, faucibus ipsum. Vestibulum interdum nunc at magna dapibus, eget malesuada nisl commodo. Vivamus vel ipsum eros. In finibus rutrum nunc, et consectetur sem imperdiet ut. Aliquam rhoncus, lorem sit amet eleifend egestas, eros tortor vehicula velit, ultrices vehicula lorem magna sed nisl. Sed vehicula turpis in leo placerat consectetur. Praesent et placerat augue, vel efficitur est. Etiam felis tellus, aliquet ac fermentum sit amet, consectetur a magna. Aliquam nibh felis, luctus sed nunc ut, convallis auctor urna. Praesent vel libero vestibulum, efficitur erat non, egestas ex.',
      attachedCertificate: new CertificateAttachedProduct({
        certificate: certificate as Certificate,
        descriptionsHTML: [
          new TextMultipleLangItem({ langKey: Language.GERMAN, text: 'Zertifikat Beschreibung de' }),
          new TextMultipleLangItem({ langKey: Language.ENGLISH, text: 'Zertifikat Beschreibung en' }),
        ],
      }),
      appointments: [
        new Appointment({
          start: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000 - 2 * 60 * 60 * 1000),
          end: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000),
        }),
        new Appointment({
          start: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000 - 2 * 60 * 60 * 1000),
          end: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000),
        }),
      ],
    });

    const musterUser = new User({
      email: 'max@mustermann.de',
      title: 'Dr.',
      firstName: 'Max',
      lastName: 'Mustermann',
      dateOfBirth: new Date(),
    });

    try {
      // @ts-expect-error TODO: fix types
      const fileBuffer = await renderCertificate(musterWorkshop, musterUser as any);
      res.setHeader('content-type', 'application/pdf');
      return res.end(fileBuffer);
    } catch (e) {
      logger.warn(e);
      return res.status(500).send('could not generate certificate');
    }
  });

  router.use('/api/certificate', async (req, res, next) => {
    const userID = req.path.split('/')[1];
    const workshopID = req.path.split('/')[2];
    if (!workshopID || !userID) {
      logger.error('No workshopID or userID given for certificate render');
      return res.sendStatus(422);
    }

    const requestContext = await getContextFromRequest(req);
    if (!requestContext?.userID) return res.sendStatus(401);

    const workshop = await getWorkshopByID(workshopID)({
      _id: 1,
      institutionID: 1,
      name: 1,
      attachedCertificate: {
        certificateID: 1,
        descriptionsHTML: { langKey: 1, text: 1 },
        certificate: { _id: 1, file: { _id: 1, meta: { extension: 1 } } },
      },
      translatedDescriptionHTML: { langKey: 1, text: 1 },
      appointments: { name: 1, start: 1, end: 1, location: 1 },
    });

    if (!workshop) throw new Error('No workshop found for ID ' + workshopID);

    const mayRenderCertificate = await runInContext(requestContext.sessionID, async () => {
      const currentUser = await getCurrentUserServerOrFail();
      if (currentUser._id === userID) {
        const participationCollection = mongo().collection(Participation._collectionName);
        const participation = await participationCollection.findOne({
          userID,
          productID: workshopID,
          state: ParticipationState.participated,
        });
        return !!participation;
      }
      return (
        currentUser.hasPermission(Permission.VIEW_ADMIN_INSTITUTION_WORKSHOPS, workshop.institutionID) ||
        currentUser.hasPermission(Permission.MANAGE_PARTICIPATION, workshop.institutionID)
      );
    });
    if (!mayRenderCertificate) return res.sendStatus(403);

    // fetch certificate again, because resolver is deactivated
    workshop!.attachedCertificate!.certificate = await getCertificateByID(workshop.attachedCertificate!.certificateID)({
      _id: 1,
      fileID: 1,
      file: { _id: 1, meta: { extension: 1 } },
    });

    const user = await getUserByID(userID)({ _id: 1, title: 1, email: 1, firstName: 1, lastName: 1, dateOfBirth: 1 });

    if (!workshop || !user) {
      logger.error('No workshop or user found for ID ' + workshopID + ' or ' + userID);
      return res.sendStatus(422);
    }

    try {
      const fileBuffer = await renderCertificate(workshop, user);
      res.setHeader('content-type', 'application/pdf');
      return res.end(fileBuffer);
    } catch (e) {
      logger.warn(e);
      return res.status(500).send('could not generate certificate');
    }
  });

  // render with user data
  return router;
}

export async function getAllowedCertificates(user: ServerCachedUser) {
  const institutionIDs = await Institution.getAllowedInstitutions(user, true);
  const aggregation = [
    { $match: { institutionID: { $in: institutionIDs } } },
    { $group: { _id: null, ids: { $push: '$_id' } } },
  ];

  const certificateCollection = mongo().collection(Certificate._collectionName);
  const result = await certificateCollection.aggregate(aggregation).toArray();
  return result.at(0)?.ids ?? [];
}

export namespace CertificateApi {
  export const subscribeByInstitution = subscribeCertificatesByInstitution;
  export const sendToParticipant = sendCertificateToParticipant;
  export const sendToProductParticipantsWhichHaveParticipated =
    sendCertificateToProductParticipantsWhichHaveParticipated;
  export const getFileID = getFileIDByCertificateID;
}

export const getCertificatesByIDs = registerQuery({
  operationName: 'getCertificatesByIDs',
  type: () => [Certificate],
  params: [{ name: 'ids', type: () => [ID] }],
  resolve:
    (ids: string[]) =>
    <SelSet extends BaseQLSelectionSet<Certificate>>(gqlFields: SelSet) =>
      readEntitiesByIDs(Certificate, ids, gqlFields),
});

export const subscribeCertificatesByIDs = registerSubscription({
  operationName: 'subscribeCertificatesByIDs',
  type: () => [Certificate],
  params: [{ name: 'ids', type: () => [ID] }],
  resolve:
    (ids: string[]) =>
    <SelSet extends BaseQLSelectionSet<Certificate>>(gqlFields: SelSet) =>
      watchEntitiesByIDs(Certificate, ids, gqlFields),
});

export const getAllCertificates = registerQuery({
  operationName: 'getAllCertificates',
  type: () => [Certificate],
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'excludeIDs', type: () => [ID] },
  ],
  resolve:
    (skip?: number, limit?: number, excludeIDs?: string[]) =>
    <SelSet extends BaseQLSelectionSet<Certificate>>(gqlFields: SelSet) =>
      readAllEntities(Certificate, gqlFields, { skip, limit, excludeIDs }),
});

export const subscribeAllCertificates = registerSubscription({
  operationName: 'subscribeAllCertificates',
  type: () => [Certificate],
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'excludeIDs', type: () => [ID] },
  ],
  resolve:
    (skip?: number, limit?: number, excludeIDs?: string[]) =>
    <SelSet extends BaseQLSelectionSet<Certificate>>(gqlFields: SelSet) =>
      watchAllEntities(Certificate, gqlFields, { skip, limit, excludeIDs }),
});

export const getCertificateByID =
  (id: string | null | undefined) =>
  async <SelSet extends BaseQLSelectionSet<Certificate>>(gqlFields: SelSet) => {
    if (!id) return null;
    return (await getCertificatesByIDs([id])(gqlFields))[0] ?? null;
  };

export const subscribeCertificateByID =
  (id: string | null | undefined) =>
  <SelSet extends BaseQLSelectionSet<Certificate>>(gqlFields: SelSet) =>
    id ? subscribeCertificatesByIDs([id])(gqlFields).pipe(map(entities => entities[0] ?? null)) : of(null);
